import type { ReactElement, ReactNode } from "react";

import { EmbeddedPageSkeleton } from "@/components/skeleton-page/EmbeddedPageSkeleton";
import { FormFlowSkeleton } from "@/components/skeleton-page/FormFlowSkeleton";
import { SkeletonPage } from "@/components/skeleton-page/SkeletonPage";
import type { RouteSkeletonConfig } from "@/types/routeSkeleton";
import { SkeletonVariant } from "@/types/routeSkeleton";

export type PageSkeletonProps =
  | {
      variant: typeof SkeletonVariant.List;
      showPrimaryAction?: boolean;
      showSearch?: boolean;
      /** Table body from the page. Omit while the route chunk is still loading. */
      grid?: ReactNode;
      className?: string;
    }
  | {
      variant: typeof SkeletonVariant.Form;
      hasSteps?: boolean;
      className?: string;
    }
  | {
      variant: typeof SkeletonVariant.Shell;
      className?: string;
    }
  | {
      variant: typeof SkeletonVariant.Embedded;
      tallSummary?: boolean;
      className?: string;
    };

/** Page-level loading skeleton. List routes do not invent a fake table; pass `grid` when you have one. */
export function PageSkeleton(props: PageSkeletonProps): ReactElement {
  if (props.variant === SkeletonVariant.List) {
    const { showPrimaryAction, showSearch, grid, className } = props;
    return (
      <SkeletonPage
        variant="list"
        showPrimaryAction={showPrimaryAction}
        showSearch={showSearch}
        grid={grid}
        className={className}
      />
    );
  }

  if (props.variant === SkeletonVariant.Form) {
    return <FormFlowSkeleton className={props.className} hasSteps={props.hasSteps} />;
  }

  if (props.variant === SkeletonVariant.Embedded) {
    return <EmbeddedPageSkeleton tallSummary={props.tallSummary} className={props.className} />;
  }

  const { className } = props;
  return <SkeletonPage variant="shell" className={className} />;
}

/** Builds a {@link PageSkeleton} from route skeleton config (typically `route.handle`). */
export function pageSkeletonFromRoute(config?: RouteSkeletonConfig): ReactElement {
  const variant = config?.skeletonVariant ?? SkeletonVariant.Embedded;

  if (variant === SkeletonVariant.List) {
    return (
      <PageSkeleton
        variant={SkeletonVariant.List}
        showPrimaryAction={config?.skeletonListShowPrimaryAction ?? false}
        showSearch={config?.skeletonListShowSearch ?? false}
      />
    );
  }

  if (variant === SkeletonVariant.Form) {
    return <PageSkeleton variant={SkeletonVariant.Form} />;
  }

  if (variant === SkeletonVariant.Shell) {
    return <PageSkeleton variant={SkeletonVariant.Shell} />;
  }

  return <PageSkeleton variant={SkeletonVariant.Embedded} />;
}
